/* ============================================================
   BORA Skin — motion layer
   Modules: smooth-scroll (Lenis) → scroll reveals → product image
            morph (view transitions) → magnetic buttons → hero parallax
   Spec: docs/superpowers/specs/2026-10-02-motion-design.md
   Everything no-ops under prefers-reduced-motion. Hidden reveal states
   only exist under html.motion-ready, which this file sets — if it never
   runs, the page is simply static.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  root.classList.add('motion-ready');


  /* === smooth scroll === */
  // Mouse / trackpad only. Touch devices keep native scrolling.
  // Panels that scroll on their own are excluded; horizontal gestures
  // (carousels) are left to the browser.
  var PREVENT = '#mobile-menu, #search-overlay, .cart-drawer__panel, .filter-drawer, .megamenu, [data-predictive-results], [data-lenis-prevent]';

  if (finePointer && typeof window.Lenis === 'function') {
    var header = document.getElementById('header');
    var lenis = new window.Lenis({
      autoRaf: true,
      lerp: 0.1,
      allowNestedScroll: true,
      anchors: { offset: -((header ? header.offsetHeight : 0) + 16) },
      prevent: function (el) { return el.matches(PREVENT); },
      virtualScroll: function (d) { return Math.abs(d.deltaX) <= Math.abs(d.deltaY); }
    });
    if (window.BORA) window.BORA.lenis = lenis;

    // Drawers lock scrolling with body overflow:hidden — pause Lenis with them,
    // otherwise it would keep scrolling the page underneath.
    var syncLock = function () {
      if (document.body.style.overflow === 'hidden') lenis.stop();
      else lenis.start();
    };
    new MutationObserver(syncLock).observe(document.body, { attributes: true, attributeFilter: ['style'] });
  }


  /* === scroll reveals === */
  // Elements are picked by selector (no Liquid markup needed):
  //   title — heading text rises from behind a mask line
  //   up    — fade + rise
  //   img   — fade + settle from a slight zoom
  // GROUPS reveal all their children together (staggered) when the row
  // enters; STAGGER grids reveal card by card as each one scrolls in.
  var TITLES = '.bestsellers__title, .new-arrivals__title, .categories__title, .concerns__title, .brendet__title, .why-bora__title, .pdp__related-title';
  var FADES = '.eyebrow:not(.hero__eyebrow), .bestsellers__viewall, .new-arrivals__viewall, .brendet__cta, .brand-spotlight__content, .footer__cols';
  var IMAGES = '.cat-tile__media img, .brand-spotlight__media img';
  var GROUPS = '.bestsellers__track, .trust-strip__inner, .concerns__grid, .categories__grid, .cat-mini-grid, .why-bora__reasons, .why-bora__reviews, .brand-spotlight__products';
  var STAGGER = '.new-arrivals__grid, .plp__grid, .pdp__related-grid';
  var STEP = 60;      // ms between staggered items
  var MAX_STEPS = 8;  // cap so long batches don't trail

  var seen = new WeakSet();
  var main = document.getElementById('main') || document.body;
  var footer = document.querySelector('.footer');

  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight && (r.width || r.height);
  }

  function wrapTitle(el) {
    if (el.querySelector(':scope > .rv-line')) return;
    var line = document.createElement('span');
    var inner = document.createElement('span');
    line.className = 'rv-line';
    inner.className = 'rv-line__in';
    while (el.firstChild) inner.appendChild(el.firstChild);
    line.appendChild(inner);
    el.appendChild(line);
  }

  function finish(el) {
    el.removeAttribute('data-rv');
    el.removeAttribute('data-rv-in');
    el.style.removeProperty('--rv-d');
  }

  function play(el, delay) {
    el.style.setProperty('--rv-d', delay + 'ms');
    el.setAttribute('data-rv-in', '');
    // Clean up once done so the element is back to its normal styles
    // (hover transforms, OOS states, etc. are never fought).
    var done = function (e) {
      if (e.target !== el && !(e.target.classList && e.target.classList.contains('rv-line__in'))) return;
      el.removeEventListener('animationend', done);
      finish(el);
    };
    el.addEventListener('animationend', done);
  }

  function mark(el, type) {
    if (type === 'title') wrapTitle(el);
    el.setAttribute('data-rv', type);
  }

  var io = new IntersectionObserver(function (entries) {
    var batch = entries
      .filter(function (e) { return e.isIntersecting; })
      .map(function (e) { return e.target; });
    if (!batch.length) return;
    batch.sort(function (a, b) {
      var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
      return (ra.top - rb.top) || (ra.left - rb.left);
    });
    batch.forEach(function (el, i) {
      io.unobserve(el);
      if (el.hasAttribute('data-rv-group')) {
        el.removeAttribute('data-rv-group');
        Array.prototype.forEach.call(el.children, function (child, k) {
          if (child.getAttribute('data-rv')) play(child, Math.min(k, MAX_STEPS) * STEP);
        });
      } else {
        play(el, Math.min(i, MAX_STEPS) * STEP);
      }
    });
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });

  // animateVisible: content swapped in later (filters, pagination) animates
  // even when it lands inside the viewport. On first load anything already
  // visible is left alone, so nothing blinks.
  function scan(scope, animateVisible) {
    function each(selector, fn) {
      Array.prototype.forEach.call(scope.querySelectorAll(selector), function (el) {
        if (seen.has(el)) return;
        seen.add(el);
        fn(el);
      });
    }
    function single(type) {
      return function (el) {
        if (!animateVisible && inView(el)) return;
        mark(el, type);
        io.observe(el);
      };
    }

    each(TITLES, single('title'));
    each(FADES, single('up'));
    each(IMAGES, single('img'));
    each(GROUPS, function (group) {
      if (!animateVisible && inView(group)) return;
      Array.prototype.forEach.call(group.children, function (child) { mark(child, 'up'); });
      group.setAttribute('data-rv-group', '');
      io.observe(group);
    });
    // Grid items are re-scanned on their own so cards appended to an
    // existing grid (load more / filters) are picked up too.
    Array.prototype.forEach.call(scope.querySelectorAll(STAGGER), function (grid) {
      Array.prototype.forEach.call(grid.children, function (card) {
        if (seen.has(card)) return;
        seen.add(card);
        if (!animateVisible && inView(card)) return;
        mark(card, 'up');
        io.observe(card);
      });
    });
  }

  scan(main, false);
  if (footer) scan(footer, false);

  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      scan(main, true);
    });
  }).observe(main, { childList: true, subtree: true });


  /* === product image morph (cross-document view transitions) === */
  // On leaving for a product page, the clicked card's image is named
  // `product-hero`; the inline script in theme.liquid names the PDP's main
  // image the same on arrival, so the browser morphs one into the other.
  function cardImage(handle) {
    var cards = document.querySelectorAll('.product-card[data-product-handle]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute('data-product-handle') !== handle) continue;
      var img = cards[i].querySelector('.product-card__media img');
      if (img && inView(img)) return img;
    }
    return null;
  }

  window.addEventListener('pageswap', function (e) {
    if (!e.viewTransition || !e.activation || !e.activation.entry) return;
    var match;
    try { match = new URL(e.activation.entry.url).pathname.match(/\/products\/([^/]+)/); } catch (err) { return; }
    if (!match) return;
    var handle = decodeURIComponent(match[1]);
    var img = cardImage(handle);
    if (!img) return;
    img.style.viewTransitionName = 'product-hero';
    try { sessionStorage.setItem('bora.vt.product', handle); } catch (err) {}
  });

  // Coming back via the back button restores this page from cache with the
  // name still set — clear it so the next transition starts clean.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll('.product-card__media img'), function (img) {
      img.style.viewTransitionName = '';
    });
  });

  // Warm the PDP's main image on hover / touch so the morph lands on a
  // loaded picture. Mirrors the srcset + sizes in templates/product.liquid.
  var warmed = new WeakSet();
  function warm(card) {
    if (warmed.has(card)) return;
    warmed.add(card);
    var img = card.querySelector('.product-card__media img');
    var src = img && (img.currentSrc || img.src);
    if (!src || !/[?&]width=\d+/.test(src)) return;
    var pre = new Image();
    pre.sizes = '(min-width: 1024px) 50vw, 90vw';
    pre.srcset = [500, 750, 1000].map(function (w) {
      return src.replace(/([?&]width=)\d+/, '$1' + w) + ' ' + w + 'w';
    }).join(', ');
  }
  function warmFrom(e) {
    var card = e.target.closest && e.target.closest('.product-card:not(.is-oos)');
    if (card) warm(card);
  }
  document.addEventListener('pointerover', warmFrom, { passive: true });
  document.addEventListener('touchstart', warmFrom, { passive: true });


  /* === magnetic buttons === */
  // Primary / secondary buttons lean a few px toward the cursor.
  if (finePointer) {
    var MAGNET = '.btn-primary, .btn-secondary';
    var magnetised = new WeakSet();
    document.addEventListener('pointerover', function (e) {
      var btn = e.target.closest && e.target.closest(MAGNET);
      if (!btn || magnetised.has(btn)) return;
      magnetised.add(btn);
      btn.addEventListener('pointermove', function (ev) {
        var r = btn.getBoundingClientRect();
        var x = (ev.clientX - (r.left + r.width / 2)) / r.width;
        var y = (ev.clientY - (r.top + r.height / 2)) / r.height;
        btn.style.translate = (x * 10).toFixed(1) + 'px ' + (y * 8).toFixed(1) + 'px';
      });
      btn.addEventListener('pointerleave', function () { btn.style.translate = ''; });
    }, { passive: true });
  }


  /* === hero parallax === */
  // Desktop only: the hero product image drifts slower than the page.
  var heroImg = document.querySelector('.hero__img--main');
  if (heroImg && finePointer && window.matchMedia('(min-width: 1024px)').matches) {
    var ticking = false;
    var paint = function () {
      ticking = false;
      var y = window.scrollY;
      if (y > window.innerHeight * 1.5) return;
      heroImg.style.translate = '0 ' + Math.min(y * 0.1, 60).toFixed(1) + 'px';
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(paint); }
    }, { passive: true });
    paint();
  }
})();
